import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Pagination,
  RiCheckLine,
  Spinner,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from 'component-library';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { usePostJobSubscription } from '@/api/rest/resources/jobSubscriptions';
import { JobProduct } from '@/api/rest/resources/types/jobProducts';
import { Form } from '@/components';
import { SuspenseBoundary } from '@/components/SuspenseBoundary';
import { JOB_PRODUCTS_QUERY_KEY, useJobProducts } from '@/hooks/useJobProducts';
import { JOB_SUBSCRIPTIONS_QUERY_KEY } from '@/hooks/useJobSubscriptions';
import { usePlotPortfolioSubscriptions } from '@/hooks/usePlotPortfolioSubscriptions';

import { JobSubscriptionFormFields, JobSubscriptionFormValues } from './JobSubscriptionFormFields';

const PRODUCT_PAGE_SIZE = 5;

type AddJobSubscriptionDialogProps = {
  open: boolean;
  onClose: () => void;
  organizationId: string;
};

export const AddJobSubscriptionDialog = ({ open, onClose, organizationId }: AddJobSubscriptionDialogProps) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className='min-w-[45vw]'>
        <DialogHeader>
          <DialogTitle>{t('shared.ndiOrganizationSubscriptions.dialog.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {open && (
            <SuspenseBoundary fallback={<Spinner size={10} />}>
              <AddJobSubscriptionFormContent organizationId={organizationId} onClose={onClose} />
            </SuspenseBoundary>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

const AddJobSubscriptionFormContent = ({
  organizationId,
  onClose,
}: {
  organizationId: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const [selectedProduct, setSelectedProduct] = useState<JobProduct | undefined>(undefined);

  return (
    <Stack spacing={4}>
      <Stack spacing={2}>
        <span className='typography-body2 font-medium'>{t('shared.ndiOrganizationSubscriptions.dialog.product')}</span>
        <SuspenseBoundary fallback={<Spinner size={10} />}>
          <JobProductTable selectedId={selectedProduct?.id} onSelect={setSelectedProduct} />
        </SuspenseBoundary>
      </Stack>
      {selectedProduct && (
        <JobSubscriptionOverrideForm
          key={selectedProduct.id}
          organizationId={organizationId}
          jobProduct={selectedProduct}
          onClose={onClose}
        />
      )}
    </Stack>
  );
};

const JobProductTable = ({
  selectedId,
  onSelect,
}: {
  selectedId?: string;
  onSelect: (jobProduct: JobProduct) => void;
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const { data } = useJobProducts({
    limit: PRODUCT_PAGE_SIZE,
    offset: (currentPage - 1) * PRODUCT_PAGE_SIZE,
  });
  const jobProducts = data.results;

  return (
    <Stack spacing={3}>
      <div className='rounded-2xl bg-white-100 border border-neutral-black-12'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className='w-px' />
              <TableHead className='typography-overline h-12 px-4 py-3 text-left align-middle leading-4 text-text-secondary'>
                {t('shared.ndiOrganizationSubscriptions.fields.displayName')}
              </TableHead>
              <TableHead className='typography-overline h-12 px-4 py-3 text-left align-middle leading-4 text-text-secondary'>
                {t('shared.ndiProducts.fields.initialSetupCostCredits')}
              </TableHead>
              <TableHead className='typography-overline h-12 px-4 py-3 text-left align-middle leading-4 text-text-secondary'>
                {t('shared.ndiOrganizationSubscriptions.fields.jobCreationCostCredits')}
              </TableHead>
              <TableHead className='typography-overline h-12 px-4 py-3 text-left align-middle leading-4 text-text-secondary'>
                {t('shared.ndiOrganizationSubscriptions.fields.jobExecutionCostCredits')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobProducts.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className='typography-body1 p-4 text-center text-text-secondary'>
                  {t('shared.ndiProducts.emptyState')}
                </TableCell>
              </TableRow>
            )}
            {jobProducts.map((jobProduct) => (
              <TableRow
                key={jobProduct.id}
                data-testid='job-subscription-product-row'
                className={cn(
                  'border-b border-divider cursor-pointer hover:bg-neutral-black-4',
                  jobProduct.id === selectedId && 'bg-neutral-hover',
                )}
                onClick={() => onSelect(jobProduct)}
              >
                <TableCell className='w-px pl-4'>
                  {jobProduct.id === selectedId && <RiCheckLine className='text-primary-100' />}
                </TableCell>
                <TableCell className='typography-body1 p-4 align-middle font-medium'>
                  {jobProduct.display_name}
                </TableCell>
                <TableCell className='typography-body1 p-4 align-middle'>
                  {jobProduct.initial_setup_cost_credits}
                </TableCell>
                <TableCell className='typography-body1 p-4 align-middle'>
                  {jobProduct.job_creation_cost_credits}
                </TableCell>
                <TableCell className='typography-body1 p-4 align-middle'>
                  {jobProduct.job_execution_cost_credits}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Pagination
        className='typography-overline text-text-secondary mx-auto'
        currentPage={currentPage}
        totalCount={data.count}
        pageSize={PRODUCT_PAGE_SIZE}
        onPageChange={setCurrentPage}
      />
    </Stack>
  );
};

const JobSubscriptionOverrideForm = ({
  organizationId,
  jobProduct,
  onClose,
}: {
  organizationId: string;
  jobProduct: JobProduct;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const postMutation = usePostJobSubscription();
  const { data: plotPortfolioSubscriptionsData } = usePlotPortfolioSubscriptions({ org: organizationId });
  const plotPortfolioSubscriptions = plotPortfolioSubscriptionsData.results;

  const form = useForm<JobSubscriptionFormValues>({
    defaultValues: {
      ndi_plot_portfolio_subscription: '',
      display_name: jobProduct.display_name,
      start_date: '',
      end_date: '',
      job_creation_cost_credits: String(jobProduct.job_creation_cost_credits),
      job_execution_cost_credits: String(jobProduct.job_execution_cost_credits),
    },
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await postMutation.mutateAsync({
        bodyData: {
          org: organizationId,
          job_product: jobProduct.id,
          ndi_plot_portfolio_subscription: values.ndi_plot_portfolio_subscription,
          display_name: values.display_name,
          start_date: values.start_date,
          end_date: values.end_date,
          job_creation_cost_credits: Number(values.job_creation_cost_credits),
          job_execution_cost_credits: Number(values.job_execution_cost_credits),
        },
      });

      queryClient.invalidateQueries({ queryKey: [JOB_SUBSCRIPTIONS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [JOB_PRODUCTS_QUERY_KEY] });
      toast({ type: 'success', title: t('shared.ndiOrganizationSubscriptions.toast.createSuccess') });
      onClose();
    } catch {
      toast({ type: 'error', title: t('shared.ndiOrganizationSubscriptions.toast.saveError') });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className='flex flex-col gap-4'>
        <JobSubscriptionFormFields control={form.control} plotPortfolioSubscriptions={plotPortfolioSubscriptions} />
        <Button
          type='submit'
          className='self-end w-fit px-6'
          loading={postMutation.isPending}
          disabled={postMutation.isPending}
        >
          {t('global.ui.buttons.save')}
        </Button>
      </form>
    </Form>
  );
};
